import MoreVertIcon from '@mui/icons-material/MoreVert';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { SortPopover } from 'components/shared/SortPopOver';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, { useMemo, useState } from 'react';
import { Utilities } from 'utilities/utilities';
import DeleteIcon from '../../../../../../assets/delete-red.png';
import { ReactComponent as ViewIcon } from '../../../../../../assets/view.svg';
import { AdminService } from '../../../../../../service/admin.service';
import zeplinColor from '../../../../../../theme/Color';
import CustomAlert from '../../../../../shared/CustomAlert/CustomAlert';
import CustomAlertContain from '../../../../../shared/CustomAlert/CustomAlertContain';
import CustomCheckbox from '../../../../../shared/CustomCheckbox/CustomCheckbox';
import CustomPopOver from '../../../../../shared/CustomPopOver/CustomPopOver';
import { LightTooltip } from '../../../../../shared/CustomTooltip/CustomTooltip';
import { IOSSwitch } from '../../../../../shared/IOSSwitch';
import AddUserDrawer from '../AddUserDrawer/AddUserDrawer';
import BlockConfirmationModal from './BlockConfirmationModal';
import ChangeRoleModal from './ChangeRoleModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import FloatingActionBar from './FloatingActionBar';
import Pagination from './Pagination';
import UserDetailsDrawer from './UserDetailsDrawer/UserDetailsDrawer';

type User = {
  id?: string;
  name?: string | null;
  username?: string | null;
  role?: { roleId: string; roleName: string } | null;
  email: string | null;
  contactedServices?: boolean | null;
  mobileNumber?: string | null;
  lastLogin?: string | null;
  status?: 'active' | 'blocked';
  profileImage?: string | null;
};

interface Column {
  id:
    | 'name'
    | 'role'
    | 'email'
    | 'connectedServices'
    | 'mobileNumber'
    | 'lastLogin'
    | 'status'
    | 'actions';
  label: string;
  minWidth?: number;
  align?: 'center' | 'left' | 'right';
  format?: (value: any) => string;
}

const columns: readonly Column[] = [
  { id: 'name', label: 'Name' },
  { id: 'role', label: 'Role' },
  { id: 'email', label: 'Email' },
  { id: 'connectedServices', label: 'Connected Services', align: 'center' },
  { id: 'mobileNumber', label: 'Mobile Number' },
  { id: 'lastLogin', label: 'Last Login' },
  { id: 'status', label: 'Status', align: 'center', minWidth: 150 },
  { id: 'actions', label: '', minWidth: 50 },
];

type UserData = {
  users: User[];
};
interface UserTableProps {
  users: UserData;
  rowsPerPage: number;
  setRowsPerPage: (value: number) => void;
  count: number;
  refreshUsers: () => Promise<void>;
  totalPages?: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  currentPage: number;
  setSortBy?: (value: string[]) => void;
  setSortOrder?: (value: ('asc' | 'desc')[]) => void;
  selectedLetter?: string;
  setSelectedLetter?: (value: string) => void;
}

const UserTable = ({
  users,
  rowsPerPage,
  setRowsPerPage,
  count,
  refreshUsers,
  totalPages = 1,
  setCurrentPage,
  currentPage,
  setSortBy,
  setSortOrder: setParentSortOrder,
  selectedLetter: parentSelectedLetter = 'All',
  setSelectedLetter: setParentSelectedLetter,
}: UserTableProps) => {
  const screenHeight = window.innerHeight;
  const [selected, setSelected] = useState<string[]>([]);
  const selectedLetter = parentSelectedLetter;

  const [pageWindowStart, setPageWindowStart] = useState(0);
  const [nameSortAnchorEl, setNameSortAnchorEl] = useState<null | HTMLElement>(null);
  const [roleSortAnchorEl, setRoleSortAnchorEl] = useState<null | HTMLElement>(null);
  const [lastLoginSortAnchorEl, setLastLoginSortAnchorEl] = useState<null | HTMLElement>(null);
  const [nameSortOption, setNameSortOption] = useState<'asc' | 'desc'>('asc');
  const [roleSortOption, setRoleSortOption] = useState<'asc' | 'desc'>('asc');
  const [lastLoginSortOption, setLastLoginSortOption] = useState<'asc' | 'desc'>('desc');
  const [rowActionAnchorEl, setRowActionAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [userToBlock, setUserToBlock] = useState<User | null>(null);
  const [isBlocking, setIsBlocking] = useState(true);
  const [isBatchOperation, setIsBatchOperation] = useState(false);
  const [changeRoleModalOpen, setChangeRoleModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userDetailsDrawerOpen, setUserDetailsDrawerOpen] = useState(false);
  const [selectedUserForDetails, setSelectedUserForDetails] = useState<User | null>(null);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);
  const [statusOverrides, setStatusOverrides] = useState<Record<string, 'active' | 'blocked'>>({});
  const [deletedKeys, setDeletedKeys] = useState<string[]>([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertError, setAlertError] = useState(false);

  const data = useMemo(() => {
    const base = users?.users || [];
    return base
      .filter((u) => {
        const key = u?.email || u?.id || '';
        return key ? !deletedKeys.includes(key) : true;
      })
      .map((u) => {
        const key = u?.email || u?.id || '';
        const overrideStatus = key ? statusOverrides[key] : undefined;
        return overrideStatus ? { ...u, status: overrideStatus } : u;
      });
  }, [users, statusOverrides, deletedKeys]);

  const alphabet = ['All', ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))];

  // Data is already filtered by letter from API - use data directly
  const filteredData = data;

  // Data is already paginated and sorted from API - just use filteredData directly
  const paginatedData = filteredData;

  const handleNameSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setNameSortAnchorEl(event.currentTarget);
  };

  const handleRoleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setRoleSortAnchorEl(event.currentTarget);
  };

  const handleLastLoginSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setLastLoginSortAnchorEl(event.currentTarget);
  };

  const handleRowActionClick = (event: React.MouseEvent<HTMLElement>, userId: string) => {
    event.stopPropagation();
    setRowActionAnchorEl(event.currentTarget);
    setSelectedUserId(userId);
  };

  const handleRowActionClose = () => {
    setRowActionAnchorEl(null);
    setSelectedUserId(null);
  };

  const handleViewDetails = () => {
    const user = data.find((u) => (u.email || u.id) === selectedUserId);
    if (user) {
      setSelectedUserForDetails(user);
      setUserDetailsDrawerOpen(true);
    }
    handleRowActionClose();
  };

  const handleEditUser = () => {
    const user = data.find((u) => (u.email || u.id) === selectedUserId);
    if (user) {
      setUserToEdit(user);
      setEditDrawerOpen(true);
    }
    handleRowActionClose();
  };

  const handleDeleteUser = () => {
    const user = data.find((u) => (u.email || u.id) === selectedUserId);
    if (user) {
      setUserToDelete(user);
      setDeleteModalOpen(true);
    }
    handleRowActionClose();
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const showToast = (message: string, isError = false) => {
    setAlertMessage(message);
    setAlertError(isError);
    setAlertOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      const successMessage = isBatchOperation
        ? 'Users deleted successfully'
        : 'User deleted successfully';
      if (isBatchOperation && selected.length > 0) {
        await AdminService.batchDeleteUsers(selected);
      } else if (userToDelete) {
        const email = userToDelete.email || '';
        await AdminService.deleteUser(email);
      }
      await refreshUsers();
      showToast(successMessage);
      setDeleteModalOpen(false);
      setIsBatchOperation(false);
      setSelected([]);
      setUserToDelete(null);
    } catch (error) {
      showToast('Failed to delete user(s).', true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setUserToDelete(null);
    setIsBatchOperation(false);
  };

  const handleSortOptionChange = (
    field: 'firstname' | 'role' | 'lastLogin',
    order: 'asc' | 'desc',
  ) => {
    // Map UI field names to API field names and build arrays
    const fieldMapping: Record<string, string[]> = {
      firstname: ['firstName'],
      role: ['role'],
      lastLogin: ['lastLogin'],
    };

    const apiFields = fieldMapping[field] || [field];
    const orders = apiFields.map(() => order);

    // Notify parent of sort change with arrays
    if (setSortBy) {
      setSortBy(apiFields);
    }
    if (setParentSortOrder) {
      setParentSortOrder(orders);
    }

    if (field === 'firstname') {
      setNameSortOption(order);
    } else if (field === 'role') {
      setRoleSortOption(order);
    } else if (field === 'lastLogin') {
      setLastLoginSortOption(order);
    }
  };

  const handleStatusToggle = async (userId: string, currentStatus: 'active' | 'blocked') => {
    if (!userId) return;
    const user = data.find((u) => (u.email || u.id) === userId);
    if (user) {
      setUserToBlock(user);
      setIsBlocking(currentStatus === 'active');
      setBlockModalOpen(true);
    }
  };

  const handleConfirmBlock = async () => {
    try {
      setLoading(true);
      const action = isBlocking ? 'blocked' : 'unblocked';
      const subject = isBatchOperation && selected.length > 0 ? 'users' : 'user';
      const successMessage = `Successfully ${action} ${subject}`;
      if (isBatchOperation && selected.length > 0) {
        if (isBlocking) {
          await AdminService.batchBlockUsers(selected);
        } else {
          await AdminService.batchUnblockUsers(selected);
        }
      } else if (userToBlock) {
        const email = userToBlock.email || '';
        if (isBlocking) {
          await AdminService.blockUser(email);
        } else {
          await AdminService.unBlockUser(email);
        }
      }
      await refreshUsers();
      showToast(successMessage);
      setBlockModalOpen(false);
      setIsBatchOperation(false);
      setSelected([]);
      setUserToBlock(null);
    } catch (error) {
      showToast('Failed to update user status.', true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBlock = () => {
    setBlockModalOpen(false);
    setUserToBlock(null);
    setIsBatchOperation(false);
  };

  const handleChangeRole = () => {
    if (selected.length === 0) return;
    setChangeRoleModalOpen(true);
  };

  const handleConfirmChangeRole = async (role: string) => {
    if (selected.length === 0) return;
    try {
      setLoading(true);
      await AdminService.batchUpdateRole({ emails: selected, roleId: role });
      await refreshUsers();
      setChangeRoleModalOpen(false);
      setSelected([]);
    } catch (error) {
      console.error('Failed to change user roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelChangeRole = () => {
    setChangeRoleModalOpen(false);
  };

  const handleBulkBlock = () => {
    if (selected.length === 0) return;
    setIsBlocking(true);
    setIsBatchOperation(true);
    setBlockModalOpen(true);
  };

  const handleBulkUnblock = () => {
    if (selected.length === 0) return;
    setIsBlocking(false);
    setIsBatchOperation(true);
    setBlockModalOpen(true);
  };

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  const handleFirstPage = () => {
    setCurrentPage(0);
    setPageWindowStart(0);
  };

  const handleLastPage = () => {
    setCurrentPage(totalPages - 1);
    const maxWindowStart = Math.max(0, totalPages - 4);
    setPageWindowStart(maxWindowStart);
  };

  // Helper function to get user initials
  const getInitials = (name: string | null) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  // Initialize dayjs relative time plugin
  dayjs.extend(relativeTime);

  // Helper function to format last login timestamp
  const formatLastLogin = (lastLogin: string | null | undefined) => {
    if (!lastLogin) return 'N/A';
    return Utilities.formatDate(dayjs(lastLogin));
  };

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: '0px', height: '100%', width: '100%' }}
    >
      <Box
        sx={{
          backgroundColor: zeplinColor.Neutral3,
          height: 40,
          display: 'flex',
          gap: '10px',
          justifyContent: 'space-between',
          borderRadius: '50px',
          padding: '8px',
          overflowX: 'auto',
          alignItems: 'center',
          flexShrink: 0,
          marginBottom: '16px',
        }}
      >
        {alphabet.map((letter) => (
          <Box
            key={letter}
            onClick={() => {
              if (setParentSelectedLetter) {
                setParentSelectedLetter(letter);
              }
            }}
            sx={{ cursor: 'pointer', minWidth: 'fit-content' }}
          >
            {selectedLetter === letter ? (
              <Chip
                label={
                  <Typography
                    variant='responsiveBody3'
                    color={zeplinColor.Neutral1}
                    sx={{ textAlign: 'center' }}
                  >
                    {letter}
                  </Typography>
                }
                sx={{
                  backgroundColor: zeplinColor.BasicBlack,
                  padding: '4px 8px',
                  borderRadius: '38px',
                  height: 'auto',
                  '& .MuiChip-label': {
                    padding: '0px',
                  },
                }}
              />
            ) : (
              <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                {letter}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
      <Paper
        sx={{
          width: '100%',
          border: 'none',
          boxShadow: 'none',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: { xs: 'calc(100vh - 350px)', xl: 'calc(100vh - 380px)' },
        }}
      >
        <TableContainer
          sx={{
            overflow: 'auto',
            flex: 1,
          }}
        >
          <Table stickyHeader aria-label='sticky table'>
            <TableHead>
              <TableRow
                sx={{
                  border: '1px solid black',
                  borderRadius: '14px',
                  '& th': {
                    backgroundColor: '#f5f5f5',
                  },
                  '& th:first-of-type': {
                    borderRadius: '14px 0px 0px 14px',
                  },
                  '& th:last-child': {
                    borderRadius: '0px 14px 14px 0px',
                  },
                }}
              >
                <TableCell padding='checkbox' sx={{ width: '50px' }}>
                  <CustomCheckbox
                    label=''
                    checked={
                      (paginatedData?.length > 0 && selected?.length === paginatedData?.length) ||
                      false
                    }
                    onChange={(checked) => {
                      const allIds = paginatedData?.map((val) => val?.email || '') || [];
                      setSelected(checked ? allIds : []);
                    }}
                  />
                </TableCell>
                {columns.map((column) => {
                  const isSortableColumn =
                    column.id === 'name' || column.id === 'role' || column.id === 'lastLogin';
                  let sortAnchor = null;
                  let handleSortClick = null;

                  if (column.id === 'name') {
                    sortAnchor = nameSortAnchorEl;
                    handleSortClick = handleNameSortClick;
                  } else if (column.id === 'role') {
                    sortAnchor = roleSortAnchorEl;
                    handleSortClick = handleRoleSortClick;
                  } else if (column.id === 'lastLogin') {
                    sortAnchor = lastLoginSortAnchorEl;
                    handleSortClick = handleLastLoginSortClick;
                  }

                  return (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                    >
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: Boolean(sortAnchor) ? '6px 8px' : '0px',
                          borderRadius: Boolean(sortAnchor) ? '8px' : '0px',
                          backgroundColor: Boolean(sortAnchor)
                            ? zeplinColor.Neutral5
                            : 'transparent',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {column.label}
                        {isSortableColumn && handleSortClick && (
                          <Box
                            onClick={handleSortClick}
                            sx={{
                              width: 16,
                              height: 16,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                            }}
                          >
                            <UnfoldMoreIcon
                              sx={{
                                fontSize: 16,
                                color: zeplinColor.BasicBlack,
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, ind) => {
                const isSelected = selected.includes(row.email || '');
                const isBlocked = row.status === 'blocked';
                const prevRowBlocked =
                  ind > 0 ? paginatedData[ind - 1]?.status === 'blocked' : false;
                const nextRowBlocked =
                  ind < paginatedData.length - 1
                    ? paginatedData[ind + 1]?.status === 'blocked'
                    : false;

                const shouldShowTopBorder = isBlocked && !prevRowBlocked;
                const shouldShowBottomBorder = isBlocked && !nextRowBlocked;

                return (
                  <TableRow
                    hover
                    role='checkbox'
                    tabIndex={-1}
                    key={row.id}
                    selected={isSelected}
                    sx={{
                      '&:last-child td, &:last-child th': { borderBottom: '2px solid #ddd' },
                      backgroundColor: row.status === 'blocked' ? zeplinColor.BlockBG : 'none',
                      '&.MuiTableRow-hover,&.MuiTableRow-hover:hover': {
                        backgroundColor: row.status === 'blocked' ? zeplinColor.BlockBG : undefined,
                      },
                    }}
                  >
                    <TableCell
                      padding='checkbox'
                      sx={
                        isBlocked
                          ? {
                              ...(shouldShowTopBorder && {
                                borderTop: `1px solid ${zeplinColor.Error}`,
                              }),

                              borderBottom: `1px solid ${zeplinColor.Error}`,
                            }
                          : undefined
                      }
                    >
                      <CustomCheckbox
                        label=''
                        checked={isSelected}
                        onChange={(checked) => {
                          setSelected((prev) =>
                            checked
                              ? [...prev, row.email || '']
                              : prev.filter((id) => id !== (row.email || '')),
                          );
                        }}
                      />
                    </TableCell>
                    {columns.map((column) => {
                      const value =
                        column.id === 'role' ? row?.role?.roleName : row[column.id as keyof User];
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          sx={
                            isBlocked
                              ? {
                                  ...(shouldShowTopBorder && {
                                    borderTop: `1px solid ${zeplinColor.Error}`,
                                  }),

                                  borderBottom: `1px solid ${zeplinColor.Error}`,
                                }
                              : undefined
                          }
                        >
                          {column.id === 'name' ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: '10px',
                                  backgroundColor: row.profileImage
                                    ? 'transparent'
                                    : zeplinColor.Brand1,
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  overflow: 'hidden',
                                  flexShrink: 0,
                                }}
                              >
                                {row.profileImage ? (
                                  <img
                                    src={row.profileImage}
                                    alt='profile'
                                    onError={(e) => {
                                      if (!e.currentTarget.src.includes('user-selected.svg')) {
                                        e.currentTarget.src = '/assets/user-selected.svg';
                                        e.currentTarget.style.backgroundColor =
                                          zeplinColor.Neutral5;
                                        e.currentTarget.style.objectFit = 'contain';
                                        e.currentTarget.style.padding = '8px';
                                      }
                                    }}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      borderRadius: '10px',
                                    }}
                                  />
                                ) : (
                                  <Typography
                                    variant='responsiveBody3'
                                    color={zeplinColor.Neutral1}
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {getInitials(row?.name || '')}
                                  </Typography>
                                )}
                              </Box>
                              <Typography
                                variant='responsiveBody3'
                                color={zeplinColor.BasicBlack}
                                style={{ fontWeight: 510 }}
                              >
                                {typeof value === 'string' ? value : row.name || 'N/A'}
                              </Typography>
                            </Box>
                          ) : column.id === 'role' ? (
                            <Box>
                              <Chip
                                label={
                                  <Typography
                                    variant='responsiveLabel1'
                                    color={zeplinColor.Neutral1}
                                    sx={{
                                      textAlign: 'center',
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                    }}
                                  >
                                    {typeof value === 'string' ? value : 'N/A'}
                                  </Typography>
                                }
                                sx={{
                                  backgroundColor: zeplinColor.BasicBlack,
                                  padding: '5px 12px',
                                  borderRadius: '50px',
                                  height: '24px',
                                }}
                              />
                            </Box>
                          ) : column.id === 'connectedServices' ? (
                            (() => {
                              const services = value as Record<string, boolean> | null | undefined;
                              const serviceIconMap: Record<string, string> = {
                                salesforce: '/assets/salesforce.svg',
                                slack: '/assets/slack.svg',
                                googleCalendar: '/assets/google-calendar.svg',
                                msTeams: '/assets/ms-teams.svg',
                                zoom: '/assets/zoom.svg',
                              };
                              const hasServices =
                                services && Object.values(services).some((v) => v === true);
                              const connectedServices = services
                                ? Object.entries(services)
                                    .filter(([_, isConnected]) => isConnected)
                                    .map(([serviceName]) => ({
                                      name: serviceName,
                                      displayName: serviceName
                                        .replace(/([A-Z])/g, ' $1')
                                        .replace(/^./, (str) => str.toUpperCase())
                                        .trim(),
                                      icon:
                                        serviceIconMap[serviceName] ||
                                        '/assets/information-circle.svg',
                                    }))
                                : [];
                              return (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '8px',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      backgroundColor: hasServices
                                        ? zeplinColor.Success
                                        : zeplinColor.AlertInfo,
                                    }}
                                  />
                                  <Typography
                                    variant='responsiveBody3'
                                    color={zeplinColor.BasicBlack}
                                  >
                                    {hasServices ? 'Yes' : 'No'}
                                  </Typography>
                                  <LightTooltip
                                    title={
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          flexDirection: 'column',
                                          gap: '8px',
                                        }}
                                      >
                                        {connectedServices.length > 0 ? (
                                          connectedServices.map((service) => (
                                            <Box
                                              key={service.name}
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                              }}
                                            >
                                              <img
                                                src={service.icon}
                                                alt={service.displayName}
                                                width='16px'
                                                height='16px'
                                                style={{ objectFit: 'contain' }}
                                              />
                                              <Typography
                                                variant='responsiveBody3'
                                                color={zeplinColor.Neutral1}
                                              >
                                                {service.displayName}
                                              </Typography>
                                            </Box>
                                          ))
                                        ) : (
                                          <Typography
                                            variant='responsiveBody3'
                                            color={zeplinColor.Neutral1}
                                          >
                                            No services connected
                                          </Typography>
                                        )}
                                      </Box>
                                    }
                                    placement='bottom-end'
                                    style={{
                                      backgroundColor: zeplinColor.BasicBlack,
                                      borderRadius: '8px',
                                    }}
                                  >
                                    <img
                                      width={'18px'}
                                      height={'18px'}
                                      src={'/assets/information-circle.svg'}
                                      alt='info'
                                      style={{ objectFit: 'fill', cursor: 'pointer' }}
                                    />
                                  </LightTooltip>
                                </Box>
                              );
                            })()
                          ) : column.id === 'status' ? (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '12px',
                                minHeight: '44px',
                              }}
                            >
                              <IOSSwitch
                                checked={value === 'active'}
                                onChange={() =>
                                  handleStatusToggle(row.email || '', value as 'active' | 'blocked')
                                }
                              />
                              <Typography
                                variant='responsiveBody3'
                                color={zeplinColor.BasicBlack}
                                sx={{ whiteSpace: 'nowrap', width: 52, textAlign: 'left' }}
                              >
                                {value === 'active' ? 'Active' : 'Blocked'}
                              </Typography>
                            </Box>
                          ) : column.id === 'lastLogin' ? (
                            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                              {formatLastLogin(value as string | null)}
                            </Typography>
                          ) : column.id === 'actions' ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <IconButton
                                size='small'
                                onClick={(e) => handleRowActionClick(e, row.email || '')}
                              >
                                <MoreVertIcon sx={{ fontSize: 20, color: zeplinColor.Neutral9 }} />
                              </IconButton>
                            </Box>
                          ) : (
                            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                              {typeof value === 'string'
                                ? value
                                : typeof value === 'boolean'
                                  ? value
                                    ? 'Yes'
                                    : 'No'
                                  : 'N/A'}
                            </Typography>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {/* Pagination Controls */}
      <Pagination
        setCurrentPage={setCurrentPage}
        setRowsPerPage={setRowsPerPage}
        currentPage={currentPage}
        totalPages={totalPages || 1}
        rowsPerPage={rowsPerPage}
        count={count}
        paginatedDataLength={paginatedData.length}
        onFirstPage={handleFirstPage}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        onLastPage={handleLastPage}
        onPageChange={setCurrentPage}
        onRowsPerPageChange={(newRows) => {
          setRowsPerPage(newRows);
          setCurrentPage(0);
        }}
        pageWindowStart={pageWindowStart}
        setPageWindowStart={setPageWindowStart}
      />

      {/* Name Sort Popover */}
      <SortPopover
        anchorEl={nameSortAnchorEl}
        setAnchorEl={setNameSortAnchorEl}
        sortOption={nameSortOption}
        onSortChange={(value) => {
          handleSortOptionChange('firstname', value);
          setNameSortAnchorEl(null);
        }}
        label='Name'
        ascLabel='A to Z'
        descLabel='Z to A'
      />

      {/* Role Sort Popover */}
      <SortPopover
        anchorEl={roleSortAnchorEl}
        setAnchorEl={setRoleSortAnchorEl}
        sortOption={roleSortOption}
        onSortChange={(value) => {
          handleSortOptionChange('role', value);
          setRoleSortAnchorEl(null);
        }}
        label='Role'
        ascLabel='A to Z'
        descLabel='Z to A'
      />

      {/* Last Login Sort Popover */}
      <SortPopover
        anchorEl={lastLoginSortAnchorEl}
        setAnchorEl={setLastLoginSortAnchorEl}
        sortOption={lastLoginSortOption}
        onSortChange={(value: any) => {
          handleSortOptionChange('lastLogin', value);
          setLastLoginSortAnchorEl(null);
        }}
        label='Last Login'
        ascLabel='Oldest to Newest'
        descLabel='Newest to Oldest'
      />

      {/* Row Actions Popover */}
      <CustomPopOver
        setAnchorEl={setRowActionAnchorEl}
        anchorEl={rowActionAnchorEl}
        id='simple-popover'
        vertical='bottom'
        horizontal='right'
        sx={{}}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: '180px',
            padding: '16px 16px',
            gap: '16px',
          }}
        >
          <Box
            onClick={handleViewDetails}
            sx={{
              // padding: '4px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              // '&:hover': {
              //   backgroundColor: zeplinColor.Neutral3,
              // },
              transition: 'background-color 0.2s ease',
            }}
          >
            <ViewIcon width={18} height={18} />
            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
              View Details
            </Typography>
          </Box>
          <Box
            onClick={handleEditUser}
            sx={{
              // padding: '4px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              // '&:hover': {
              //   backgroundColor: zeplinColor.Neutral3,
              // },
              transition: 'background-color 0.2s ease',
            }}
          >
            <img src={'/assets/edit.svg'} width={18} height={18} />
            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
              Edit
            </Typography>
          </Box>
          <Box
            onClick={handleDeleteUser}
            sx={{
              // padding: '4px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              // '&:hover': {
              //   backgroundColor: zeplinColor.Neutral3,
              // },
              transition: 'background-color 0.2s ease',
            }}
          >
            <img src={DeleteIcon} width={18} height={18} />
            <Typography variant='responsiveBody3' color={zeplinColor.Error}>
              Delete
            </Typography>
          </Box>
        </Box>
      </CustomPopOver>

      <DeleteConfirmationModal
        open={deleteModalOpen}
        userName={userToDelete?.name || ''}
        userCount={isBatchOperation ? selected.length : undefined}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={loading}
      />
      <BlockConfirmationModal
        open={blockModalOpen}
        userName={userToBlock?.name || userToBlock?.email || ''}
        userCount={isBatchOperation ? selected.length : undefined}
        isBlocking={isBlocking}
        onConfirm={handleConfirmBlock}
        onCancel={handleCancelBlock}
        loading={loading}
      />
      <ChangeRoleModal
        open={changeRoleModalOpen}
        onConfirm={handleConfirmChangeRole}
        onCancel={handleCancelChangeRole}
        loading={loading}
      />
      <UserDetailsDrawer
        open={userDetailsDrawerOpen}
        user={selectedUserForDetails}
        onClose={() => setUserDetailsDrawerOpen(false)}
        refreshUsers={refreshUsers}
        onDeleteSuccess={() => showToast('User deleted successfully')}
        onEditClick={() => {
          if (selectedUserForDetails) {
            setUserToEdit(selectedUserForDetails);
            setEditDrawerOpen(true);
            setUserDetailsDrawerOpen(false);
          }
        }}
      />
      <AddUserDrawer
        open={editDrawerOpen}
        onClose={() => {
          setEditDrawerOpen(false);
          setUserToEdit(null);
        }}
        onAddSuccess={(operation) => {
          refreshUsers();
          showToast(operation === 'edit' ? 'User updated successfully' : 'User added successfully');
        }}
        mode='edit'
        userData={
          userToEdit
            ? {
                id: userToEdit.id,
                firstName: userToEdit.name?.split(' ')[0],
                lastName: userToEdit.name?.split(' ').slice(1).join(' '),
                email: userToEdit.email ?? undefined,
                mobile: userToEdit.mobileNumber ?? undefined,
                roleId: typeof userToEdit.role === 'object' ? userToEdit.role?.roleId : undefined,
                profileImage: userToEdit.profileImage ?? undefined,
              }
            : undefined
        }
      />
      <CustomAlert
        open={alertOpen}
        handleClose={handleAlertClose}
        message={
          <CustomAlertContain
            alertIcon={
              alertError ? (
                <img src='/assets/error.svg' width={24} height={24} />
              ) : (
                <img src='/assets/checkmark-green.png' width={24} height={24} />
              )
            }
            alert={alertMessage}
            error={alertError}
          />
        }
        error={alertError}
      />
      <FloatingActionBar
        selectedCount={selected.length}
        onClearSelection={() => setSelected([])}
        onChangeRole={handleChangeRole}
        onUnblock={handleBulkUnblock}
        onBlock={handleBulkBlock}
        onDelete={() => {
          setIsBatchOperation(true);
          setDeleteModalOpen(true);
        }}
      />
    </Box>
  );
};

export default UserTable;
